import {CSSProperties} from 'react';

/**
 * Compute the inline `style` object for the EagGrid wrapper div.
 *
 * ### Height decision matrix
 *
 * | Condition | Result |
 * |---|---|
 * | `autoHeight` is true | No height — grid expands with content |
 * | `availableHeightPx === 0` | No height — grid is below the fold or not yet measured; let `h-full` fill the parent |
 * | `parentHeight > 0` and `availableHeightPx >= parentHeight` | No height — the parent's explicit height is the binding constraint; `h-full` fills it correctly |
 * | otherwise | `{height: availableHeightPx}` — the viewport edge is the binding constraint; cap the grid |
 *
 * ### Width decision matrix
 *
 * | Condition | Result |
 * |---|---|
 * | `availableWidthPx > 0` | `{maxWidth: availableWidthPx}` — constrain to measured parent width |
 * | otherwise | No width — let CSS `max-w-full` (100%) handle it |
 *
 * ### Why this matters
 *
 * **Height:** Without this logic the grid would overflow the viewport when rendered in a
 * tall parent (e.g. a full-height Card).  Without the parent-height comparison
 * the Business Cases grid (inside `<Box height={100}>` ≈ 400px) would get a
 * viewport-sized inline height instead of filling its 400px container.
 *
 * **Width:** When EagGrid is placed inside a flex-row layout (e.g. `Sections` with a sidebar),
 * the CSS `width: 100%` on ancestor boxes can resolve to the entire parent width rather than
 * the remaining space. The `availableWidthPx` measurement (from `useAvailableWidthPx`) computes
 * the true available horizontal space and applies it as `maxWidth`, preventing overflow.
 *
 * @param autoHeight - When true the grid sizes itself to its content; no height
 *   cap is ever applied.
 * @param availableHeightPx - Pixels from the wrapper's top to the viewport
 *   bottom (from `useAvailableHeightPx`).  `0` means unmeasured / below fold.
 * @param parentHeight - `offsetHeight` of the wrapper's `parentElement`.  `0`
 *   when the parent has no explicit height (flex child with `flex:1` etc.).
 * @param availableWidthPx - Pixels from the wrapper's left edge to the parent's
 *   right edge (from `useAvailableWidthPx`). `0` means unmeasured. Defaults to `0`.
 */
export function getContainerStyle(
  autoHeight: boolean,
  availableHeightPx: number,
  parentHeight: number,
  availableWidthPx: number = 0
): CSSProperties | undefined {
  let heightStyle: CSSProperties | undefined;
  if (!autoHeight && availableHeightPx > 0) {
    if (parentHeight <= 0 || availableHeightPx < parentHeight) {
      heightStyle = {height: availableHeightPx};
    }
  }

  const widthStyle: CSSProperties | undefined =
    availableWidthPx > 0 ? {maxWidth: availableWidthPx} : undefined;

  if (!heightStyle && !widthStyle) {
    return undefined;
  }
  return {...heightStyle, ...widthStyle};
}

/**
 * Compute the `className` string for the EagGrid wrapper div.
 *
 * Non-autoHeight grids always get `h-full` (Tailwind `height: 100%`) so they
 * fill their bounded parent — matching the old datagrid's `height: 100%`
 * behaviour.  When the viewport edge is the binding constraint, an inline
 * `style.height` from {@link getContainerStyle} overrides `h-full` via
 * CSS specificity.  autoHeight grids intentionally have no height constraint.
 *
 * ### Stacking context isolation
 *
 * Both variants include `isolate` (CSS `isolation: isolate`) to create a new
 * stacking context on the grid wrapper.  Without it, internal z-indices
 * (e.g. sticky action cells at z-9, action header at z-120, the grid's own
 * sidebar panel at z-301) leak into the page's stacking context and can
 * overlap external elements like the DMS activity sidebar (z-100).
 *
 * @param wrapperStyle - Visual style variant from grid definition: `'INLINE'`
 *   omits the card border/shadow; `'CARD'` (default) includes them.
 * @param autoHeight - When true no `h-full` is added.
 * @param isMobile - When true always uses the inline (borderless) variant.
 */
export function getWrapperClassName(
  wrapperStyle: 'INLINE' | 'CARD',
  autoHeight: boolean,
  isMobile: boolean
): string {
  const heightFallback = autoHeight ? '' : ' h-full';
  const inlineClass = `w-full max-w-full min-w-0 flex flex-col overflow-hidden isolate bg-white${heightFallback}`;
  const cardClass = `w-full max-w-full min-w-0 flex flex-col overflow-hidden isolate bg-white border border-slate-200 shadow-sm rounded${heightFallback}`;

  if (isMobile) {
    return inlineClass;
  }
  return wrapperStyle === 'INLINE' ? inlineClass : cardClass;
}
